import React, { useContext, useCallback } from 'react'
import {
  address,
  pipe,
  appendTransactionMessageInstruction,
  createTransactionMessage,
  setTransactionMessageFeePayerSigner,
  setTransactionMessageLifetimeUsingBlockhash,
  signAndSendTransactionMessageWithSigners,
  assertIsTransactionMessageWithSingleSendingSigner,
  getBase58Decoder,
} from '@solana/kit'
import { useWalletAccountTransactionSendingSigner } from '@solana/react'
import { type UiWalletAccount } from '@wallet-standard/react'
import { RpcContext } from '@/contexts/SolanaRpcContext'
import { ChainContext } from '@/contexts/SolanaChainContextProvider'
import {
  findAssociatedTokenPda,
  getTransferInstruction,
  getTransferCheckedInstruction,
} from '@solana-program/token'
import {
  getSolanaTokenConfig,
  SOLANA_TOKEN_2022_PROGRAM,
} from '@/utils/currencies'
import * as Sentry from '@sentry/react'

interface UnifiedWalletButtonProps {
  amount: number
  recipientAddress: string
  tokenSymbol: string
  senderAddress: string
  orderId: string
  onTransferComplete?: (txHash: string) => void
  onError?: (error: Error) => void
}

export const ConnectedSolanaWalletButton: React.FC<{
  selectedAccount: UiWalletAccount
  props: UnifiedWalletButtonProps
  showToast: (
    title: string,
    description: string,
    type: 'info' | 'warning' | 'success'
  ) => void
  setWalletDialogOpen: (open: boolean) => void
  disconnectCurrentWallet: () => void
  isSolanaProcessing: boolean
  setIsSolanaProcessing: (processing: boolean) => void
}> = ({
  selectedAccount,
  props,
  showToast,
  isSolanaProcessing,
  setIsSolanaProcessing,
}) => {
  const { chain: currentChain, solanaExplorerClusterName } =
    useContext(ChainContext)
  const { rpc } = useContext(RpcContext)

  // This hook is now safely called with a valid selectedAccount
  const transactionSendingSigner = useWalletAccountTransactionSendingSigner(
    selectedAccount,
    currentChain
  )

  // Handle Solana transfer with a valid signer
  const handleSolanaTransfer = useCallback(async () => {
    if (!transactionSendingSigner) {
      showToast(
        'Wallet Connection Error',
        'There was an issue with your wallet connection. Please try reconnecting.',
        'warning'
      )
      return
    }
    try {
      // Add initial breadcrumb
      Sentry.addBreadcrumb({
        category: 'wallet',
        message: 'Starting Solana transfer',
        data: {
          amount: props.amount,
          tokenSymbol: props.tokenSymbol,
          orderId: props.orderId,
          senderAddress: props.senderAddress,
          recipientAddress: props.recipientAddress,
        },
        level: 'info',
      })

      // Set transaction state
      setIsSolanaProcessing(true)

      // Get latest blockhash
      let latestBlockhash
      try {
        const response = await rpc
          .getLatestBlockhash({
            commitment: 'confirmed',
          })
          .send()
        latestBlockhash = response.value
      } catch (error) {
        console.error('Failed to get latest blockhash:', error)
        throw new Error('Failed to get latest blockhash. Please try again.')
      }

      if (solanaExplorerClusterName === 'testnet') {
        throw new Error('Solana testnet transactions are not supported')
      }
      const tokenConfig = getSolanaTokenConfig({
        cluster: solanaExplorerClusterName,
        tokenSymbol: props.tokenSymbol,
      })
      const mint = address(tokenConfig.mint)
      const tokenProgram = address(tokenConfig.tokenProgram)
      const destination = address(tokenConfig.destinationAta)

      const amountInBaseUnits = BigInt(
        props.amount.toFixed(tokenConfig.decimals).replace('.', '')
      )

      // Fetch source token account
      const sourceTokenAccountsResponse = await rpc
        .getTokenAccountsByOwner(
          address(selectedAccount.address),
          {
            mint,
          },
          {
            encoding: 'jsonParsed',
          }
        )
        .send()
      if (!sourceTokenAccountsResponse?.value?.length) {
        throw new Error(`Sender ${props.tokenSymbol} token account not found`)
      }

      // Source token account
      const sourceATA = await findAssociatedTokenPda({
        owner: address(transactionSendingSigner.address),
        tokenProgram,
        mint,
      })

      // Create transaction message
      let transactionMessage = pipe(
        createTransactionMessage({
          version: 0,
        }),
        (tx) =>
          setTransactionMessageFeePayerSigner(transactionSendingSigner, tx),
        (tx) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, tx)
      )

      // Token-2022 mints (e.g. CADC) must use transferChecked under the
      // Token-2022 program; legacy SPL tokens (e.g. USDC) use a plain transfer.
      // transferChecked is byte-compatible across both programs, so the same
      // builder targets Token-2022 via the programAddress override.
      const transferInstruction =
        tokenConfig.tokenProgram === SOLANA_TOKEN_2022_PROGRAM
          ? getTransferCheckedInstruction(
              {
                source: sourceATA[0],
                mint,
                destination,
                authority: transactionSendingSigner,
                amount: amountInBaseUnits,
                decimals: tokenConfig.decimals,
              },
              { programAddress: tokenProgram }
            )
          : getTransferInstruction({
              source: sourceATA[0],
              destination,
              authority: transactionSendingSigner,
              amount: amountInBaseUnits,
            })
      // Add transfer instruction to transaction
      transactionMessage = appendTransactionMessageInstruction(
        transferInstruction,
        transactionMessage
      )
      console.log(
        'Transaction message created with instructions count:',
        transactionMessage.instructions.length
      )

      // Sign and send the transfer transaction
      console.log('Signing and sending transfer transaction to CLI account...')
      const signedTransaction =
        await signAndSendTransactionMessageWithSigners(transactionMessage)
      assertIsTransactionMessageWithSingleSendingSigner(transactionMessage)
      const signature = getBase58Decoder().decode(signedTransaction)
      console.log(
        'Transfer transaction sent successfully! Signature:',
        signedTransaction
      )
      if (props.onTransferComplete) {
        props.onTransferComplete(signature)
      }
      showToast(
        'Transaction Complete!',
        'Your transaction has been confirmed.',
        'success'
      )
    } catch (error: any) {
      console.error('Solana transfer error:', error)
      showToast(
        'Transaction Failed',
        error?.message ||
          'An error occurred while processing your transaction.',
        'warning'
      )
      if (props.onError) {
        props.onError(error as Error)
      }

      // Capture error with Sentry
      Sentry.captureException(error, {
        tags: {
          action: 'solana_transaction',
          recipient_address: props.recipientAddress,
          currentChain: currentChain,
          signer: transactionSendingSigner.address,
          senderAddress: props.senderAddress,
        },
      })
    } finally {
      setIsSolanaProcessing(false)
    }
  }, [
    transactionSendingSigner,
    rpc,
    props.amount,
    props.recipientAddress,
    props.tokenSymbol,
    props.orderId,
    props.senderAddress,
    props.onTransferComplete,
    props.onError,
    showToast,
    currentChain,
    solanaExplorerClusterName,
    selectedAccount.address,
    setIsSolanaProcessing,
  ])

  // Handle button click for connected wallet
  const handleConnectedWalletClick = useCallback(() => {
    showToast(
      'Transaction In Progress',
      'Please do not refresh the page or submit another transaction until this one completes.',
      'info'
    )

    // Call the handler directly
    handleSolanaTransfer()
  }, [showToast, handleSolanaTransfer])
  return (
    <button
      onClick={handleConnectedWalletClick}
      disabled={isSolanaProcessing}
      className={`inline-flex items-center justify-center py-6 px-4 w-full text-center border-none dark:bg-gray-200 bg-black dark:text-black text-white`}
      data-testid="btn-connectedsolanawalletbutton-submit"
    >
      <span>{`Send ${props.amount} ${props.tokenSymbol}`}</span>
    </button>
  )
}

/*
IMPROVEMENT SUGGESTIONS:
1. Add visual feedback during transaction processing (loading spinner, progress indicator)
2. Implement transaction status indicators (pending, confirming, finalizing)
3. Add more robust error handling with specific messaging for different Solana errors
4. Consider implementing a retry mechanism for failed transactions
5. Extract the transaction logic into a custom hook to separate concerns
6. Add validation for token amount before attempting transaction
7. Implement proper transaction fee estimation and display
8. Consider adding transaction simulation before sending to validate it will succeed
9. Add timeout handling for long-running transactions
10. Implement proper fallback handling when the RPC endpoint is unavailable
11. Consider adding transaction receipt/confirmation UI after successful transactions
12. Add proper logging throughout the transaction lifecycle for better debugging
13. Implement proper handling for low balance scenarios
*/
